import { motionAllowed } from '../../lib/motion';
import { pointAt } from './rich-field';

/*
 * An image's way into the draft (InputPanel, ImageProgress.tsx). The choice pressed grows across
 * the bar and becomes the progress, a light running to and fro in it. Once the image is read, it
 * closes to a spark, and the spark leaps to where the writing is to go. Only then is the writing
 * put in, and a lit caret writes it in, the words coming up behind it (input-panel.css).
 */

const EASE_IN_OUT = 'cubic-bezier(0.65, 0, 0.35, 1)';
/** The theme's spring (--ease-spring), where the browser can't take its own. */
const SPRING = 'cubic-bezier(0.34, 1.3, 0.64, 1)';
const GROW_MS = 520;
const FADE_MS = 140;
const CLOSE_MS = 420;
/** The spark's size, and how long it's in the air. */
const SPARK = 14;
const LEAP_MS = 560;
/** A spark that never gets to land goes by itself. */
const SPARK_LIFE_MS = 4_000;
/**
 * How long from one word's arrival to the next: unhurried for a line or two, quicker for more, so
 * all of it is in within about the longest.
 */
const STEP_MS = 70;
const STEP_MIN_MS = 10;
const WRITING_MS = 1_600;
/** A word comes up just behind the caret, then takes its time (input-panel.css). */
const BEHIND_MS = 40;
const WORD_IN_MS = 700;

/** One of the theme's values, as set where `element` is, or `fallback`. */
function token(element: Element, name: string, fallback: string, kind: string): string {
  const value = getComputedStyle(element).getPropertyValue(name).trim();
  const css = (globalThis as { CSS?: Partial<typeof CSS> }).CSS;
  return value && css?.supports?.(kind, value) ? value : fallback;
}

/** The pace an image's `words` are written in at: ms from one to the next. */
export function paceFor(words: number): number {
  return Math.round(Math.min(STEP_MS, Math.max(STEP_MIN_MS, WRITING_MS / Math.max(1, words))));
}

/** The choice pressed, at `from`, grows across the bar into the progress `pill`. */
export function growFromButton(pill: HTMLElement | null, from: DOMRect | null): void {
  if (!pill || !from || !motionAllowed()) return;
  const box = pill.getBoundingClientRect();
  if (box.width === 0 || from.width === 0) return;
  pill.animate(
    [
      {
        left: `${String(from.left - box.left)}px`,
        right: `${String(box.right - from.right)}px`,
        top: `${String(from.top - box.top)}px`,
        bottom: `${String(box.bottom - from.bottom)}px`,
      },
      { left: '0px', right: '0px', top: '0px', bottom: '0px' },
    ],
    {
      duration: GROW_MS,
      easing: token(pill, '--ease-spring', SPRING, 'transition-timing-function'),
    },
  );
  pill.querySelector('.image-progress-content')?.animate(
    [
      { opacity: 0, transform: 'translateY(6px)' },
      { opacity: 1, transform: 'none' },
    ],
    {
      duration: 300,
      delay: 180,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      fill: 'backwards',
    },
  );
}

/**
 * The work done: the progress `pill`'s light and content go, and it closes to a spark, which is
 * handed over, in the same place on the page, for leapTo to carry on.
 */
export async function closeToSpark(pill: HTMLElement | null): Promise<HTMLElement | null> {
  if (!pill || !motionAllowed()) return null;
  for (const part of pill.querySelectorAll('.image-progress-scan, .image-progress-content'))
    part.animate([{ opacity: 1 }, { opacity: 0 }], { duration: FADE_MS, fill: 'forwards' });
  const box = pill.getBoundingClientRect();
  const x = `${String((box.width - SPARK) / 2)}px`;
  const y = `${String((box.height - SPARK) / 2)}px`;
  pill.classList.add('is-closing');
  await pill
    .animate(
      [
        { left: '0px', right: '0px', top: '0px', bottom: '0px' },
        { left: x, right: x, top: y, bottom: y, borderRadius: `${String(SPARK / 2)}px` },
      ],
      { duration: CLOSE_MS, easing: EASE_IN_OUT, fill: 'forwards' },
    )
    .finished.catch(() => undefined);
  if (!pill.isConnected) return null;
  const at = pill.getBoundingClientRect();
  const spark = document.createElement('span');
  spark.className = 'image-spark';
  spark.style.left = `${String(at.left)}px`;
  spark.style.top = `${String(at.top)}px`;
  document.body.append(spark);
  pill.style.visibility = 'hidden';
  setTimeout(() => {
    spark.remove();
  }, SPARK_LIFE_MS);
  return spark;
}

/** Where `offset` is in the draft `field`, as the caret would be there. */
function placeOf(field: HTMLElement, offset: number): DOMRect | null {
  const [node, at] = pointAt(field, offset);
  try {
    const range = document.createRange();
    range.setStart(node, at);
    const caret = range.getBoundingClientRect();
    if (caret.height > 0) return caret;
  } catch {
    // Where ranges can't be measured, the line it's in will do.
  }
  const box = (node instanceof Element ? node : node.parentElement)?.getBoundingClientRect();
  return box && box.height > 0 ? box : null;
}

/** A curve over to `dx`, `dy`, above whichever end is higher, shrinking a little as it lands. */
function leap(dx: number, dy: number): Keyframe[] {
  const cx = dx / 2;
  const cy = Math.min(0, dy) - 48;
  return Array.from({ length: 17 }, (_, index) => {
    const t = index / 16;
    const u = 1 - t;
    const x = 2 * u * t * cx + t * t * dx;
    const y = 2 * u * t * cy + t * t * dy;
    return {
      offset: t,
      transform: `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) scale(${(1 - 0.3 * t).toFixed(3)})`,
    };
  });
}

/**
 * The `spark` leaps to `offset` in the draft `field`, where the writing is to start. Resolves once
 * it's there, by the page's clock, for the writing to go in and be written in from then.
 */
export async function leapTo(
  spark: HTMLElement | null,
  field: HTMLElement | null,
  offset: number,
): Promise<number> {
  const target = spark?.isConnected && field && motionAllowed() ? placeOf(field, offset) : null;
  if (!spark || !target) {
    spark?.remove();
    return performance.now();
  }
  const from = spark.getBoundingClientRect();
  const rtl = field && getComputedStyle(field).direction === 'rtl';
  const x = (rtl ? target.right : target.left) - SPARK / 2;
  const y = target.top + target.height / 2 - SPARK / 2;
  await spark
    .animate(leap(x - from.left, y - from.top), {
      duration: LEAP_MS,
      easing: EASE_IN_OUT,
      fill: 'forwards',
    })
    .finished.catch(() => undefined);
  return performance.now();
}

/**
 * The writing just put in the draft `field`, drawn at its `pace` since `since` (a paced done mark,
 * rich-field.ts): a lit caret runs over its words in sight in step with their arrival, from where
 * the `spark` landed, which goes. Says how long until the last word is in.
 */
export function playLitCaret(
  field: HTMLElement | null,
  spark: HTMLElement | null,
  since: number,
  pace: number,
): number {
  spark
    ?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 160, fill: 'forwards' })
    .addEventListener('finish', () => {
      spark.remove();
    });
  const words = [...(field?.querySelectorAll<HTMLElement>('.is-paced .editor-mark-word') ?? [])];
  const indexOf = (word: HTMLElement) => Number(word.style.getPropertyValue('--word')) || 0;
  const last = Math.max(-1, ...words.map(indexOf));
  const now = performance.now();
  const done = Math.max(0, since + (last + 1) * pace + BEHIND_MS + WORD_IN_MS - now);
  if (!field || last < 0 || !motionAllowed()) return done;

  const view = field.getBoundingClientRect();
  const top = Math.max(view.top, 0);
  const bottom = Math.min(view.bottom, window.innerHeight);
  const seen = words
    .map((word) => ({ index: indexOf(word), place: word.getBoundingClientRect() }))
    .filter(({ place }) => place.width > 0 && place.top >= top - 1 && place.bottom <= bottom + 1)
    .sort((a, b) => a.index - b.index);
  const first = seen[0];
  const end = seen.at(-1);
  if (!first || !end) return done;
  const rtl = getComputedStyle(field).direction === 'rtl';
  // Over each word in the time it has, then on to the next, along the line or down to the next.
  const start = first.index * pace;
  const length = Math.max(1, (end.index + 1) * pace - start);
  const keyframes: Keyframe[] = [];
  let offset = 0;
  for (const { index, place } of seen) {
    const y = place.top + place.height * 0.1;
    const height = `${String(place.height * 0.8)}px`;
    const [from, to] = rtl ? [place.right, place.left] : [place.left, place.right];
    offset = Math.max(offset, (index * pace - start) / length);
    keyframes.push({ offset, transform: `translate(${String(from)}px, ${String(y)}px)`, height });
    offset = Math.max(offset, ((index + 0.9) * pace - start) / length);
    keyframes.push({ offset, transform: `translate(${String(to)}px, ${String(y)}px)`, height });
  }
  keyframes.push({ ...keyframes.at(-1), offset: 1 });
  const caret = document.createElement('span');
  caret.className = 'image-sweep';
  document.body.append(caret);
  const fading = keyframes.at(-1) ?? {};
  caret.animate(
    keyframes.map((frame) => ({ ...frame, opacity: 1 })),
    { duration: length, delay: since + start - now, easing: 'linear' },
  );
  caret
    .animate(
      [
        { ...fading, offset: 0, opacity: 1 },
        { ...fading, offset: 1, opacity: 0 },
      ],
      { duration: 220, delay: since + start + length - now },
    )
    .addEventListener('finish', () => {
      caret.remove();
    });
  return done;
}
